import { Feature } from '@frontend/functionality/types';
import { TransactionStateName } from '@frontend/transaction/types';
import { FormattedMessage } from 'react-intl';

export function getNivalizTypeLabel(feature: Feature | string | null | undefined): React.ReactNode {
    if (feature === Feature.INBOUND_TRANSACTION) {
        return (
            <FormattedMessage
                id='feature.nivaliz.transactions.type.coffin'
                description='Label for Nivaliz coffin (inbound) transaction type'
                defaultMessage='Coffin'
            />
        );
    }
    if (feature === Feature.OUTBOUND_TRANSACTION) {
        return (
            <FormattedMessage
                id='feature.nivaliz.transactions.type.urn'
                description='Label for Nivaliz urn (outbound) transaction type'
                defaultMessage='Urn'
            />
        );
    }
    return null;
}

/** High-contrast type chips: teal vs red — not the orange/teal used by Drop-Off / Pick-up. */
export function getNivalizTypeBadgeClass(feature: Feature | string | null | undefined): string {
    if (feature === Feature.INBOUND_TRANSACTION) return 'bg-brand-primary';
    if (feature === Feature.OUTBOUND_TRANSACTION) return 'bg-status-danger';
    return 'bg-brand-muted';
}

/**
 * Action / status chip for list cards.
 * Coffins awaiting pick-up and urns awaiting drop-off use cremation copy;
 * actionable pending states keep Pick-up / Drop-Off.
 */
export function getNivalizActionLabel(feature: Feature | string | null | undefined, state: TransactionStateName | string | null | undefined): React.ReactNode {
    if (feature === Feature.INBOUND_TRANSACTION && state === TransactionStateName.TRANSACTION_PICK_UP_PENDING) {
        return (
            <FormattedMessage
                id='mobile-nivaliz.utils.nivaliz-transaction-labels.waiting-to-be-cremated'
                description='Status chip on a personal Nivaliz coffin card when pick-up is pending (waiting for cremation)'
                defaultMessage='Waiting to be cremated'
            />
        );
    }
    if (feature === Feature.OUTBOUND_TRANSACTION && state === TransactionStateName.TRANSACTION_DROP_OFF_PENDING) {
        return (
            <FormattedMessage
                id='mobile-nivaliz.utils.nivaliz-transaction-labels.being-cremated'
                description='Status chip on a personal Nivaliz urn card when drop-off is pending (being cremated)'
                defaultMessage='Being cremated'
            />
        );
    }
    if (state === TransactionStateName.TRANSACTION_PICK_UP_PENDING) {
        return (
            <FormattedMessage
                id='Buttons.PICK_UP'
                description='Pick up text on a button'
                defaultMessage='Pick-up'
            />
        );
    }
    if (state === TransactionStateName.TRANSACTION_DROP_OFF_PENDING) {
        return (
            <FormattedMessage
                id='Buttons.DROP_OFF'
                description='Drop off text on a button'
                defaultMessage='Drop-Off'
            />
        );
    }
    return null;
}

/** Coffins awaiting drop-off and urns awaiting pick-up — highest-priority cards for the user. */
export function isNivalizActionRequiredTransaction(
    feature: Feature | string | null | undefined,
    state: TransactionStateName | string | null | undefined
): boolean {
    return (
        (feature === Feature.INBOUND_TRANSACTION && state === TransactionStateName.TRANSACTION_DROP_OFF_PENDING) ||
        (feature === Feature.OUTBOUND_TRANSACTION && state === TransactionStateName.TRANSACTION_PICK_UP_PENDING)
    );
}

interface NivalizListCardTitleArgs {
    deceasedName?: string | null;
    shippingNotes?: string | null;
    trackingNumber?: string | null;
}

/** Condensed list title: deceased name, else notes, else tracking number, else short fallback. */
export function getNivalizListCardTitle({ deceasedName, shippingNotes, trackingNumber }: NivalizListCardTitleArgs): React.ReactNode {
    const deceased = deceasedName?.trim();
    if (deceased) {
        return deceased;
    }

    const notes = shippingNotes?.trim();
    if (notes) {
        return notes;
    }

    const tracking = trackingNumber?.trim();
    if (tracking) {
        return tracking;
    }

    return (
        <FormattedMessage
            id='nivaliz.transaction.default'
            defaultMessage='Nivaliz transaction'
        />
    );
}

interface NivalizTransactionTitleArgs {
    feature: Feature | string | null | undefined;
    senderName?: string | null;
    receiverName?: string | null;
    isSender?: boolean;
    isReceiver?: boolean;
}

/**
 * Corporate list/detail title: deceased name or tracking number first,
 * otherwise coffin/urn from-sender / for-receiver wording.
 */
export function getNivalizCorporateTitle({
    deceasedName,
    trackingNumber,
    feature,
    senderName,
    receiverName
}: Pick<NivalizListCardTitleArgs, 'deceasedName' | 'trackingNumber'> &
    Pick<NivalizTransactionTitleArgs, 'feature' | 'senderName' | 'receiverName'>): React.ReactNode {
    if (deceasedName?.trim() || trackingNumber?.trim()) {
        return getNivalizListCardTitle({ deceasedName, trackingNumber });
    }
    return getNivalizTransactionTitle({ feature, senderName, receiverName });
}

/** Personal uses role; corporate omits roles and always shows from-sender / for-receiver. */
export function getNivalizTransactionTitle({
    feature,
    senderName,
    receiverName,
    isSender = false,
    isReceiver = false
}: NivalizTransactionTitleArgs): React.ReactNode {
    const sender = senderName ?? '/';
    const receiver = receiverName ?? '/';

    if (feature === Feature.INBOUND_TRANSACTION) {
        if (isSender) {
            return (
                <FormattedMessage
                    id='nivaliz.transaction.coffin_for_receiver'
                    defaultMessage='Coffin for {receiver}'
                    values={{ receiver }}
                />
            );
        }
        return (
            <FormattedMessage
                id='nivaliz.transaction.coffin_from_sender'
                defaultMessage='Coffin from {sender}'
                values={{ sender }}
            />
        );
    }

    if (feature === Feature.OUTBOUND_TRANSACTION) {
        if (isReceiver) {
            return (
                <FormattedMessage
                    id='nivaliz.transaction.urn_from_sender'
                    defaultMessage='Urn from {sender}'
                    values={{ sender }}
                />
            );
        }
        return (
            <FormattedMessage
                id='nivaliz.transaction.urn_for_receiver'
                defaultMessage='Urn for {receiver}'
                values={{ receiver }}
            />
        );
    }

    return (
        <FormattedMessage
            id='nivaliz.transaction.default'
            defaultMessage='Nivaliz transaction'
        />
    );
}
